import { UserNotificationsNotifications, UserNotificationsNotificationsEntityTypeEnum } from "@microtica/ms-notification-sdk";
import { Avatar, Badge, Card, Space } from "antd";
import { UserOutlined, ClockCircleOutlined, BellOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { useNavigate } from "react-router";
import { getNotificationService } from "../../backend";

interface NotificationProps extends UserNotificationsNotifications {
    projectName: string;
    onClose: () => void;
    updateProjectInfo: (projectId: string) => Promise<void>;
}

const Notification = (data: NotificationProps) => {
    const navigate = useNavigate();
    const automaticTrigger = "Microtica Deployments";
    const microticaSavingSchedule = "Microtica Saving Schedule";

    const renderColor = (status: string) => {
        switch (true) {
            case status.endsWith("COMPLETE"):
            case status.endsWith("DEPLOYED"):
            case status.endsWith("UNDEPLOYED"):
                return "green";
            case status.endsWith("IN_PROGRESS"):
            case status.endsWith("RUNNING"):
                return "#4775d1";
            case status.endsWith("FAILED"):
                return "red";
            default:
                return "gray";
        }
    }

    const handleOnClick = async () => {
        await Promise.all([
            getNotificationService().markNotificationAsRead(data.id, data.projectId),
            data.updateProjectInfo(data.projectId)
        ]);

        data.onClose();

        if (data.entityType === UserNotificationsNotificationsEntityTypeEnum.MICROSERVICE) {
            if (data.status === "UNDEPLOYED" || data.status === "FAILED") {
                navigate(`/projects/${data.projectId}/environments/${data.additionalInfo?.stageId}/apps`);
            } else {
                navigate(`/projects/${data.projectId}/environments/${data.additionalInfo?.stageId}/apps/${data.additionalInfo?.clusterId}/${data.additionalInfo?.namespace}/${data.itemId}`);
            }
        } else if (data.entityType === UserNotificationsNotificationsEntityTypeEnum.STAGE) {
            navigate(`/projects/${data.projectId}/environments/${data.itemId}/deployments`);
        } else {
            // We dont have a way to distinguish Service vs Resource in order to provide proper routing here
            navigate(`/projects/${data.projectId}`);
        }
        return;
    }

    const renderNotificationColor = (isRead: boolean) => {
        return isRead ? "white" : "whitesmoke";
    }

    const mapEntityType = () => {
        let value;
        switch ((data.entityType) as any) {
            case "MICROSERVICE":
                value = "APPLICATION";
                break;
            case "STAGE":
                value = "ENVIRONMENT";
                break;
            case "SCHEDULE":
                value = `${data.message.split(" ")[0]} resources`;
                break;
            default:
                value = data.entityType;
                break;
        }
        return value[0] + value.slice(1).toLowerCase();
    }

    const mapStatus = () => {
        if ([
            "ROLLBACK_COMPLETE",
            "UPDATE_ROLLBACK_COMPLETE"
        ].includes(data.status)) {
            return "rollback completed";
        } else if ([
            "DELETE_COMPLETE"
        ].includes(data.status)) {
            // the environment has a status DELETE_COMPLETE when it is being undeployed
            return "undeployed";
        } else if ([
            "CREATE_IN_PROGRESS",
            "UPDATE_IN_PROGRESS",
            "DELETE_IN_PROGRESS",
            "ROLLBACK_IN_PROGRESS",
            "UPDATE_ROLLBACK_IN_PROGRESS",
            "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS",
            "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS"
        ].includes(data.status)) {
            // these are all STAGE related statuses
            return "deployment in progress";
        } else if ([
            "RUNNING"
        ].includes(data.status)) {
            // this is a PIPELINE related status
            return "build in progress";
        } else if ([
            "FAILED",
            "UPDATE_FAILED",
            "CREATE_FAILED",
            "DELETE_FAILED",
            "ROLLBACK_FAILED",
            "UPDATE_ROLLBACK_FAILED",
        ].includes(data.status)) {
            return "failed";
        } else if ([
            "DEPLOYED",
            "CREATE_COMPLETE",
            "UPDATE_COMPLETE"
        ].includes(data.status)) {
            return "deployed";
        } else {
            // Known statuses: ABORTED, UNDEPLOYED
            return data.status.toLocaleLowerCase();
        }
    }

    const notificationTitle = `${mapEntityType()} ${mapStatus()}`;

    return (
        <Card
            type="inner"
            style={{ marginBottom: 6, cursor: "pointer", backgroundColor: renderNotificationColor(data.isRead) }}
            onClick={handleOnClick}
        >
            <Space style={{ fontSize: "smaller" }} align={"start"} >
                <div style={{ marginRight: 16 }}>
                    <Badge dot={!data.isRead}>
                        <Avatar
                            style={{ backgroundColor: renderColor(data.status) }}
                            icon={<BellOutlined />}
                        />
                    </Badge>
                </div>
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {notificationTitle}
                    </div>
                    <div>
                        {data.message}
                    </div>
                    <div style={{ fontSize: 11, color: "gray" }}>
                        <div>
                            {data.projectName}
                        </div>
                        {
                            ![automaticTrigger, microticaSavingSchedule].includes(data.author) &&
                            <Space size={3}>
                                <UserOutlined />
                                {data.author}
                            </Space>
                        }
                        <Space size={3}>
                            <ClockCircleOutlined />
                            {moment(data.timestamp, "x").fromNow()}
                        </Space>
                    </div>
                </div>
            </Space>
        </Card>
    )
}

export default Notification;